<script setup>
import { ref, onMounted, computed } from 'vue';
import { Head } from '@inertiajs/vue3';
import axios from 'axios';

// State
const tags = ref([]);
const loading = ref(true);
const saving = ref(false);
const isEditing = ref(false);
const showGuideModal = ref(false);

// Form state
const form = ref({
    id: null,
    name: '',
    description: '',
    color: '#FFA500',
    is_active: true
});

const errors = ref({});

// Methods
const loadTags = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/admin/tag/get');
        if (response.data.status === 'success') {
            tags.value = response.data.data;
        }
    } catch (error) {
        console.error('Error loading tags:', error);
    } finally {
        loading.value = false;
    }
};

const resetForm = () => {
    form.value = {
        id: null,
        name: '',
        description: '',
        color: '#FFA500',
        is_active: true
    };
    errors.value = {};
    isEditing.value = false;
};

const editTag = async (id) => {
    try {
        const response = await axios.get(`/admin/tag/${id}`);
        if (response.data.status === 'success') {
            const tag = response.data.data;
            form.value = {
                id: tag.id,
                name: tag.name,
                description: tag.description || '',
                color: tag.color,
                is_active: tag.is_active === 1
            };
            isEditing.value = true;
        }
    } catch (error) {
        alert('Gagal memuat data tag');
    }
};

const submitForm = async () => {
    saving.value = true;
    errors.value = {};
    
    const data = {
        name: form.value.name,
        description: form.value.description,
        color: form.value.color,
        is_active: form.value.is_active ? 1 : 0
    };
    
    let url = '/admin/tag';
    if (isEditing.value) {
        data._method = 'PUT';
        url = `/admin/tag/${form.value.id}`;
    }
    
    try {
        const response = await axios.post(url, data);
        if (response.data.status === 'success') {
            alert(response.data.message);
            resetForm();
            loadTags();
        } else if (response.data.status === 'info') {
            alert(response.data.message);
        }
    } catch (error) {
        if (error.response?.data?.messages) {
            alert(error.response.data.messages.join('\n'));
        } else {
            alert('Terjadi kesalahan');
        }
    } finally {
        saving.value = false;
    }
};

const toggleStatus = async (tag) => {
    try {
        const response = await axios.post('/admin/tag/toggle-status', {
            id: tag.id,
            is_active: tag.is_active === 1 ? 0 : 1
        });
        if (response.data.status === 'success') {
            loadTags();
        }
    } catch (error) {
        alert('Gagal mengubah status');
    }
};

const deleteTag = async (tag) => {
    if (!confirm(`Apakah Anda yakin ingin menghapus tag "${tag.name}"?`)) return;
    
    try {
        const response = await axios.delete(`/admin/tag/${tag.id}`);
        if (response.data.status === 'success') {
            alert(response.data.message);
            loadTags();
        } else {
            alert(response.data.message);
        }
    } catch (error) {
        alert(error.response?.data?.message || 'Gagal menghapus tag');
    }
};

onMounted(() => {
    loadTags();
});
</script>

<template>
    <Head title="Kelola Tags" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center">
                <div class="me-auto">
                    <h3 class="page-title">Kelola Tags</h3>
                    <nav>
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><a href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></a></li>
                            <li class="breadcrumb-item"><a href="/admin/news">Berita & Artikel</a></li>
                            <li class="breadcrumb-item active">Tags</li>
                        </ol>
                    </nav>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Form Panel -->
                <div class="col-xl-5 col-12">
                    <div class="card">
                        <form @submit.prevent="submitForm">
                            <div class="card-body">
                                <div class="d-flex justify-content-between align-items-center mb-0">
                                    <h4 class="card-title text-primary mb-0">
                                        <i class="ti-tag me-5"></i>Kelola Tags Artikel
                                    </h4>
                                    <button type="button" class="btn btn-outline-info btn-sm" @click="showGuideModal = true">
                                        <i class="fa fa-question-circle me-1"></i>Panduan
                                    </button>
                                </div>
                                <hr class="my-10">
                                
                                <div class="form-group mb-3">
                                    <label class="form-label">Nama Tag <span class="text-danger">*</span></label>
                                    <input type="text" v-model="form.name" class="form-control" 
                                        placeholder="Contoh: Laravel, PHP, Tutorial" required>
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label class="form-label">Deskripsi Tag (SEO Meta Description)</label>
                                    <textarea v-model="form.description" class="form-control" rows="3"
                                        placeholder="Masukkan deskripsi tag untuk SEO (120-160 karakter optimal)"></textarea>
                                    <small class="text-muted">{{ form.description?.length || 0 }}/160 karakter</small>
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label class="form-label">Warna Tag <span class="text-danger">*</span></label>
                                    <div class="input-group">
                                        <input type="color" v-model="form.color" class="form-control form-control-color">
                                        <input type="text" :value="form.color" class="form-control" readonly disabled>
                                        <div class="color-preview" :style="{ backgroundColor: form.color }"></div>
                                    </div>
                                </div>
                                
                                <div class="form-group mb-3">
                                    <div class="form-check form-switch">
                                        <input class="form-check-input" type="checkbox" v-model="form.is_active" id="is_active">
                                        <label class="form-check-label" for="is_active">Tag Aktif</label>
                                    </div>
                                    <small class="text-muted">Tag yang tidak aktif tidak akan muncul di dropdown artikel</small>
                                </div>
                            </div>
                            <div class="card-footer text-end">
                                <button v-if="isEditing" type="button" class="btn btn-primary-light me-1" @click="resetForm">
                                    <i class="ti-trash me-2"></i> Batalkan
                                </button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <i v-if="saving" class="fa fa-spinner fa-spin me-2"></i>
                                    <i v-else class="ti-save-alt me-2"></i>
                                    {{ saving ? 'Menyimpan...' : 'Simpan' }}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                
                <!-- Table Panel -->
                <div class="col-xl-7 col-12">
                    <div class="card">
                        <div class="card-body">
                            <h4 class="card-title text-primary mb-0"><i class="ti-view-list me-10"></i>Daftar Tags</h4>
                            <hr class="my-10">
                            <div class="table-responsive">
                                <table class="table table-sm table-hover">
                                    <thead>
                                        <tr>
                                            <th class="text-center">#</th>
                                            <th>Tag</th>
                                            <th class="text-center">Warna</th>
                                            <th class="text-center">Status</th>
                                            <th class="text-center">Artikel</th>
                                            <th class="text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="loading">
                                            <td colspan="6" class="text-center p-4">
                                                <div class="spinner-border text-primary"></div>
                                            </td>
                                        </tr>
                                        <tr v-else-if="tags.length === 0">
                                            <td colspan="6" class="text-center text-muted p-4">
                                                <i class="fa fa-info-circle me-2"></i>Belum ada tag
                                            </td>
                                        </tr>
                                        <tr v-else v-for="(tag, index) in tags" :key="tag.id">
                                            <td class="text-center">{{ index + 1 }}</td>
                                            <td>
                                                <div class="fw-bold">{{ tag.name }}</div>
                                                <div class="text-muted small">{{ tag.description || 'Tidak ada deskripsi' }}</div>
                                            </td>
                                            <td class="text-center">
                                                <div class="rounded-circle mx-auto" 
                                                    :style="{ width: '30px', height: '30px', backgroundColor: tag.color, border: '2px solid #ddd' }">
                                                </div>
                                            </td>
                                            <td class="text-center">
                                                <button type="button" 
                                                    :class="['btn', 'btn-sm', 'btn-toggle', tag.is_active ? 'active' : '']"
                                                    @click="toggleStatus(tag)">
                                                    <span class="handle"></span>
                                                </button>
                                            </td>
                                            <td class="text-center">{{ tag.news_count || 0 }}</td>
                                            <td class="text-center">
                                                <div class="btn-group">
                                                    <button type="button" class="btn btn-outline-primary btn-sm" @click="editTag(tag.id)">
                                                        <i class="fa fa-edit"></i>
                                                    </button>
                                                    <button type="button" class="btn btn-outline-danger btn-sm" @click="deleteTag(tag)">
                                                        <i class="fa fa-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>

    <!-- Guide Modal -->
    <div v-if="showGuideModal" class="modal fade show" style="display: block; background: rgba(0,0,0,0.5);">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title"><i class="fa fa-book me-2"></i>Panduan Pengelolaan Tags</h5>
                    <button type="button" class="btn-close" @click="showGuideModal = false"></button>
                </div>
                <div class="modal-body">
                    <div class="alert alert-info">
                        <i class="fa fa-info-circle me-2"></i>
                        <strong>Tips:</strong> Tags membantu mengorganisir artikel dan meningkatkan SEO
                    </div>
                    
                    <h6>🏷️ Apa itu Tags?</h6>
                    <ul>
                        <li>Tags adalah label untuk mengelompokkan artikel berdasarkan topik</li>
                        <li>Satu artikel bisa memiliki multiple tags</li>
                        <li>Tags membantu pembaca menemukan artikel terkait</li>
                    </ul>

                    <h6>📝 Cara Membuat Tags yang Baik</h6>
                    <ul>
                        <li><strong>Nama:</strong> Gunakan kata kunci yang relevan</li>
                        <li><strong>Konsisten:</strong> Gunakan format yang sama</li>
                        <li><strong>Limit:</strong> Gunakan 3-5 tags per artikel</li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.color-preview {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 2px solid #ddd;
    display: inline-block;
}
</style>
